import useInfiniteCursorDataQuery from '../../../hooks/useInfiniteCursorDataQuery/useInfiniteCursorDataQuery';
import { UseInfiniteCursorDataQueryOptions } from '../../../hooks/useInfiniteCursorDataQuery/useInfiniteCursorDataQuery.types';
import {
  getCustomerActivityFeedKey,
  getOrderActivityFeedKey,
  getTargetActivityFeedKey,
} from './activityFeed.keys';
import {
  getCustomerActivityFeed,
  getOrderActivityFeed,
  getTargetActivityFeed,
} from './activityFeed.repository';
import {
  ActivityFeedRowDTO,
  GetCustomerActivityFeedParams,
  GetOrderActivityFeedParams,
  GetTargetActivityFeedParams,
} from './activityFeed.types';

export const ACTIVITY_FEED_DEFAULT_LIMIT = 20;
export const ACTIVITY_FEED_MIN_LIMIT = 1;
export const ACTIVITY_FEED_MAX_LIMIT = 50;
export const ACTIVITY_FEED_STALE_TIME_IN_MS = 2 * 60 * 1000;
export const ACTIVITY_FEED_GC_TIME_IN_MS = 0;

const resolveActivityFeedLimit = (limit?: number) =>
  Math.min(
    Math.max(limit ?? ACTIVITY_FEED_DEFAULT_LIMIT, ACTIVITY_FEED_MIN_LIMIT),
    ACTIVITY_FEED_MAX_LIMIT,
  );

export const useCustomerActivityFeed = (
  params: GetCustomerActivityFeedParams,
  options?: UseInfiniteCursorDataQueryOptions,
) => {
  const limit = resolveActivityFeedLimit(params.limit);
  const resolvedParams: GetCustomerActivityFeedParams = { ...params, limit };

  return useInfiniteCursorDataQuery<ActivityFeedRowDTO>({
    limit,
    queryKey: getCustomerActivityFeedKey(resolvedParams),
    isEnabled: Boolean(params.customerId) && options?.isEnabled !== false,
    refetchOnMount: options?.refetchOnMount,
    staleTime: options?.staleTime ?? ACTIVITY_FEED_STALE_TIME_IN_MS,
    gcTime: options?.gcTime ?? ACTIVITY_FEED_GC_TIME_IN_MS,
    retry: options?.retry,
    queryFn: async ({ cursor, limit, signal }) => {
      const response = await getCustomerActivityFeed(
        { ...resolvedParams, limit, cursor: cursor ?? undefined },
        signal,
      );

      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error('failed to load the customer activity feed');
      }

      return response.data;
    },
  });
};

export const useTargetActivityFeed = (
  params: GetTargetActivityFeedParams,
  options?: UseInfiniteCursorDataQueryOptions,
) => {
  const limit = resolveActivityFeedLimit(params.limit);
  const resolvedParams: GetTargetActivityFeedParams = { ...params, limit };

  return useInfiniteCursorDataQuery<ActivityFeedRowDTO>({
    limit,
    queryKey: getTargetActivityFeedKey(resolvedParams),
    isEnabled: Boolean(params.targetId) && options?.isEnabled !== false,
    refetchOnMount: options?.refetchOnMount,
    staleTime: options?.staleTime ?? ACTIVITY_FEED_STALE_TIME_IN_MS,
    gcTime: options?.gcTime ?? ACTIVITY_FEED_GC_TIME_IN_MS,
    retry: options?.retry,
    queryFn: async ({ cursor, limit, signal }) => {
      const response = await getTargetActivityFeed(
        { ...resolvedParams, limit, cursor: cursor ?? undefined },
        signal,
      );

      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error('failed to load the plan activity feed');
      }

      return response.data;
    },
  });
};

export const useOrderActivityFeed = (
  params: GetOrderActivityFeedParams,
  options?: UseInfiniteCursorDataQueryOptions,
) => {
  const limit = resolveActivityFeedLimit(params.limit);
  const resolvedParams: GetOrderActivityFeedParams = { ...params, limit };

  return useInfiniteCursorDataQuery<ActivityFeedRowDTO>({
    limit,
    queryKey: getOrderActivityFeedKey(resolvedParams),
    isEnabled: Boolean(params.orderId) && options?.isEnabled !== false,
    refetchOnMount: options?.refetchOnMount,
    staleTime: options?.staleTime ?? ACTIVITY_FEED_STALE_TIME_IN_MS,
    gcTime: options?.gcTime ?? ACTIVITY_FEED_GC_TIME_IN_MS,
    retry: options?.retry,
    queryFn: async ({ cursor, limit, signal }) => {
      const response = await getOrderActivityFeed(
        { ...resolvedParams, limit, cursor: cursor ?? undefined },
        signal,
      );

      if (!response.ok || !response.data) {
        throw response.originalError ?? new Error('failed to load the order activity feed');
      }

      return response.data;
    },
  });
};
